import React, { useState, useEffect, useLayoutEffect } from 'react';
import { X, Star, Users, HeartHandshake, ShieldCheck, Accessibility } from 'lucide-react';
import firebaseService from '../services/firebaseService';
import { collection, getDocs } from 'firebase/firestore';
import './DemoTooltip.css';

function DemoTooltip({ onDismiss }) {
  const [isVisible, setIsVisible] = useState(false);
  const [memberCount, setMemberCount] = useState(1500);

  useEffect(() => {
    // Show tooltip after a short delay to let page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    // Fetch real user count
    fetchUserCount();

    return () => clearTimeout(timer);
  }, []);

  // Escape closes the dialog (keyboard parity with the × button).
  // Layout effect (not passive): the listener must exist before first
  // paint, otherwise a keypress in the mount commit window is missed.
  useLayoutEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsVisible(false);
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isVisible, onDismiss]);

  const fetchUserCount = async () => {
    try {
      const users = await firebaseService.getUsers();
      const count = users.length;
      // Round to nearest 100
      const roundedCount = Math.round(count / 100) * 100;
      setMemberCount(roundedCount);
    } catch (error) {
      console.error('Error fetching user count:', error);
      // Keep default if error
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };

  const handleGetStarted = () => {
    setIsVisible(false);
    onDismiss();
  };

  const handleLogin = () => {
    // Redirect to login page
    window.location.href = '/auth';
  };

  if (!isVisible) return null;

  return (
    <div
      className="demo-tooltip-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to The Crapper Mapper"
    >
      <div className="demo-tooltip">
        <div className="demo-tooltip-header">
          <h3>Welcome to The Crapper Mapper!</h3>
          <button
            className="demo-tooltip-close"
            onClick={handleDismiss}
            aria-label="Close welcome dialog"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="demo-tooltip-content">
          <div className="demo-tooltip-icon-section">
            <div className="demo-tooltip-main-icon">
              <img src="/app-icon.png" alt="The Crapper Mapper" />
            </div>
          </div>
          
          <div className="demo-tooltip-section">
            <h2>Never gamble on a public restroom again.</h2>
            <p>Find the cleanest bathrooms nearby, reviewed by real people.</p>
          </div>
          
          <div className="demo-tooltip-features">
            <div className="feature-item">
              <div className="feature-icon">
                <Star size={24} />
              </div>
              <span>Cleanliness Ratings</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Accessibility size={24} />
              </div>
              <span>Accessibility Info</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Users size={24} />
              </div>
              <span>Family Friendly</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <HeartHandshake size={24} />
              </div>
              <span>Community Reviewed</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <ShieldCheck size={24} />
              </div>
              <span>Verified Locations</span>
            </div>
          </div>
          
          {memberCount > 0 && (
            <div className="demo-tooltip-community">
              <div className="community-avatars">
                <div className="avatar">
                  <img src="https://i.pravatar.cc/150?img=12" alt="" />
                </div>
                <div className="avatar">
                  <img src="https://i.pravatar.cc/150?img=33" alt="" />
                </div>
                <div className="avatar">
                  <img src="https://i.pravatar.cc/150?img=59" alt="" />
                </div>
                <div className="avatar">
                  <img src="https://i.pravatar.cc/150?img=65" alt="" />
                </div>
              </div>
              <p><strong>{memberCount.toLocaleString()}+ members</strong> helping each other <strong>Doo Better</strong> every day.</p>
            </div>
          )}
        </div>
        
        <div className="demo-tooltip-actions">
          <button className="demo-tooltip-btn demo-tooltip-btn-primary" onClick={handleGetStarted}>
            Explore the Map
          </button>
          <button className="demo-tooltip-btn demo-tooltip-btn-secondary" onClick={handleLogin}>
            Already have an account? Login here
          </button>
        </div>
      </div>
    </div>
  );
}

export default DemoTooltip;
